import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, ArrowLeft, Save, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "./ImageUploader";
import LayoutEditor from "./LayoutEditor";
import { uploadMediaFile } from "@/lib/media";

function CtaFields({ label, value, onChange, idPrefix, hint }) {
  const v = value || {};
  const set = (k, val) => onChange(k === "action" ? { text: v.text, action: val } : { ...v, [k]: val });
  const action = v.action || (v.url ? "link" : "enquire");
  return (
    <div className="border border-zinc-200 rounded-lg p-3 space-y-2.5">
      <div className="text-xs font-medium text-zinc-600">{label}</div>
      <Input value={v.text || ""} onChange={(e) => set("text", e.target.value)} placeholder="Button text (blank = hidden)" data-testid={`${idPrefix}-text`} />
      <select value={action} onChange={(e) => set("action", e.target.value)} className="w-full text-sm border border-zinc-300 rounded-md px-3 py-2 bg-white h-10" data-testid={`${idPrefix}-action`}>
        <option value="enquire">Open enquiry form</option>
        <option value="link">Go to a URL</option>
        <option value="call">Call a phone number</option>
      </select>
      {action === "link" && <Input value={v.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="/page or https://…" data-testid={`${idPrefix}-url`} />}
      {action === "call" && <Input value={v.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+91 90000 00000 (blank = site phone from Settings)" data-testid={`${idPrefix}-phone`} />}
      {hint && <div className="text-[10px] text-zinc-400">{hint}</div>}
    </div>
  );
}

function BulkAddImages({ onAdd, testId = "bulk-add-images" }) {
  const inputRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [pct, setPct] = useState({ done: 0, total: 0 });
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (!files.length) return;
    setRunning(true); setPct({ done: 0, total: files.length });
    const urls = []; let failed = 0;
    for (let i = 0; i < files.length; i++) {
      try { urls.push(await uploadMediaFile(files[i])); }
      catch { failed += 1; }
      setPct({ done: i + 1, total: files.length });
    }
    setRunning(false);
    if (urls.length) {
      onAdd(urls);
      if (failed === 0) toast.success(`${urls.length} image${urls.length === 1 ? "" : "s"} added`);
      else toast.warning(`${urls.length} added, ${failed} failed`);
    } else if (failed) {
      toast.error(`Upload failed for all ${failed} files`);
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid={`${testId}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={running}
        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50 disabled:opacity-60" data-testid={testId}>
        {running ? `Uploading ${pct.done}/${pct.total}…` : "Upload multiple"}
      </button>
    </>
  );
}

export function PropertiesList() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/admin/properties").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post("/admin/properties", {
      slug: `new-property-${Date.now()}`, name: "New Property", theme: "kai",
      tagline: "", hero: {}, intro: {}, usps: [], rooms: [], event_spaces: [],
      gallery: [], reviews: [], faqs: [], amenities: [], pricing_packages: [],
      wedding_section: {}, corporate_section: {}, seo: {}, is_published: false,
    });
    nav(`/admin/properties/${data.id}`);
  };

  const remove = async (id) => {
    if (!confirm("Delete this property?")) return;
    await api.delete(`/admin/properties/${id}`);
    load();
  };

  const duplicate = async (p) => {
    try {
      const { data } = await api.post(`/admin/properties/${p.id}/duplicate`);
      toast.success(`Duplicated as "${data.name}" (draft)`);
      nav(`/admin/properties/${data.id}`);
    } catch {
      toast.error("Could not duplicate property");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Properties</div>
          <h1 className="font-display text-4xl mt-3">All properties</h1>
        </div>
        <button onClick={create} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md hover:bg-black" data-testid="new-property">
          <Plus className="w-4 h-4" /> New property
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={`prop-row-${p.slug}`}>
            <div className="aspect-[4/3] bg-zinc-100">
              {p.hero?.image_url && <img src={p.hero.image_url} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-xl">{p.name}</div>
                  <div className="text-xs text-zinc-500">/{p.slug}</div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded-full ${p.is_published ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                  {p.is_published ? "Published" : "Draft"}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Link to={`/admin/properties/${p.id}`} className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50">Edit</Link>
                <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View</a>
                <button onClick={() => duplicate(p)} title="Duplicate this property" className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50 inline-flex items-center gap-1" data-testid={`duplicate-prop-${p.slug}`}><Copy className="w-3 h-3" /> Duplicate</button>
                <button onClick={() => remove(p.id)} className="ml-auto text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ArrayEditor = ({ items, onChange, fields, label, testId = "array-editor" }) => {
  const update = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {})]);

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{label}</div>
        <button onClick={add} type="button" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="mt-4 space-y-4">
        {items.map((it, i) => (
          <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-md p-4 space-y-3">
            <div className="flex items-center justify-between text-xs text-zinc-500">Item {i + 1}<button type="button" onClick={() => remove(i)} className="text-red-600 hover:underline">Remove</button></div>
            {fields.map((f) => (
              f.type === "textarea" ? (
                <Textarea key={f.key} value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} placeholder={f.label} rows={3} />
              ) : f.type === "image" ? (
                <ImageUploader key={f.key} label={f.label} value={it[f.key] || ""} onChange={(v) => update(i, f.key, v)} testId={`${testId}-${i}-${f.key}`} />
              ) : f.type === "array-string" ? (
                <div key={f.key}>
                  <label className="text-xs text-zinc-500">{f.label} (comma-separated)</label>
                  <Input value={(it[f.key] || []).join(", ")} onChange={(e) => update(i, f.key, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
                </div>
              ) : (
                <Input key={f.key} value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} placeholder={f.label} />
              )
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const StringListEditor = ({ items, onChange, label, placeholder = "URL or text", testId }) => {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{label}</div>
        <button type="button" onClick={() => onChange([...(items || []), ""])} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="mt-3 space-y-2">
        {(items || []).map((v, i) => (
          <div key={i} className="flex gap-2">
            <Input value={v} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }} placeholder={placeholder} />
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="px-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export function PropertyEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/properties/${id}`).then((r) => setP(r.data));
  }, [id]);

  if (!p) return <div>Loading…</div>;

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const setNested = (k, sub, v) => setP((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), [sub]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/properties/${id}`, p);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Link to="/admin/properties" className="text-xs text-zinc-500 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All properties</Link>
      <div className="flex items-end justify-between mt-3 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Editing</div>
          <h1 className="font-display text-4xl mt-2">{p.name || "New property"}</h1>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><Switch checked={p.is_published} onCheckedChange={(v) => set("is_published", v)} data-testid="prop-published" /> Published</label>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm rounded-md disabled:opacity-60" data-testid="prop-save">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <Tabs defaultValue="basics" className="mt-8">
        <TabsList className="flex-wrap h-auto">
          {["basics", "layout", "hero", "intro", "usps", "rooms", "spaces", "gallery", "weddings", "corporate", "packages", "reviews", "faqs", "seo"].map((t) => (
            <TabsTrigger key={t} value={t} className="capitalize" data-testid={`tab-${t}`}>{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="basics" className="space-y-4 mt-6">
          <Input value={p.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Name" />
          <Input value={p.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="Slug" />
          <Input value={p.tagline || ""} onChange={(e) => set("tagline", e.target.value)} placeholder="Tagline" />
          <Input value={p.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="Location" />
          <Select value={p.theme || "kai"} onValueChange={(v) => set("theme", v)}>
            <SelectTrigger><SelectValue placeholder="Theme" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="kai">Kai (warm)</SelectItem>
              <SelectItem value="yellow_moon">Yellow Moon (nature)</SelectItem>
              <SelectItem value="white_mist">White Mist (crisp)</SelectItem>
              <SelectItem value="aviana">Aviana (modern)</SelectItem>
            </SelectContent>
          </Select>
          <StringListEditor items={p.amenities} onChange={(v) => set("amenities", v)} label="Amenities" placeholder="Pool" testId="amenities-editor" />
        </TabsContent>

        <TabsContent value="layout" className="mt-6">
          <LayoutEditor layout={p.layout} onChange={(v) => set("layout", v)} />
        </TabsContent>

        <TabsContent value="hero" className="space-y-4 mt-6">
          <ImageUploader label="Hero image" value={p.hero?.image_url} onChange={(v) => setNested("hero", "image_url", v)} testId="hero-image" />
          <Input value={p.hero?.video_url || ""} onChange={(e) => setNested("hero", "video_url", e.target.value)} placeholder="Hero video URL (optional)" />
          <Input value={p.hero?.headline || ""} onChange={(e) => setNested("hero", "headline", e.target.value)} placeholder="Headline" />
          <Textarea value={p.hero?.subheadline || ""} onChange={(e) => setNested("hero", "subheadline", e.target.value)} placeholder="Subheadline" rows={2} />
          <CtaFields label="Primary hero button" idPrefix="hero-cta"
            value={{ text: p.hero?.cta_text, action: p.hero?.cta_action, url: p.hero?.cta_url, phone: p.hero?.cta_phone }}
            onChange={(v) => { setNested("hero", "cta_text", v.text); setNested("hero", "cta_action", v.action); setNested("hero", "cta_url", v.url); setNested("hero", "cta_phone", v.phone); }} />
          <CtaFields label="Secondary hero button" idPrefix="hero-cta2" hint="Default: View gallery scrolls to the gallery. Blank button text hides it."
            value={p.hero?.cta2 || { text: "View gallery", action: "link", url: "#gallery" }}
            onChange={(v) => setNested("hero", "cta2", v)} />
        </TabsContent>

        <TabsContent value="intro" className="space-y-4 mt-6">
          <Input value={p.intro?.title || ""} onChange={(e) => setNested("intro", "title", e.target.value)} placeholder="Intro title" />
          <Textarea value={p.intro?.body || ""} onChange={(e) => setNested("intro", "body", e.target.value)} placeholder="Intro body" rows={6} />
          <ImageUploader label="Intro image" value={p.intro?.image_url} onChange={(v) => setNested("intro", "image_url", v)} testId="intro-image" />
        </TabsContent>

        <TabsContent value="usps" className="mt-6">
          <ArrayEditor items={p.usps || []} onChange={(v) => set("usps", v)}
            fields={[{ key: "icon", label: "Icon name" }, { key: "title", label: "Title" }, { key: "description", label: "Description", type: "textarea" }]} label="USPs" testId="usps-editor" />
        </TabsContent>

        <TabsContent value="rooms" className="mt-6">
          <ArrayEditor items={p.rooms || []} onChange={(v) => set("rooms", v)}
            fields={[{ key: "name", label: "Name" }, { key: "description", label: "Description", type: "textarea" }, { key: "image_url", label: "Image", type: "image" }, { key: "price_text", label: "Price text" }]} label="Rooms & Villas" testId="rooms-editor" />
        </TabsContent>

        <TabsContent value="spaces" className="mt-6">
          <ArrayEditor items={p.event_spaces || []} onChange={(v) => set("event_spaces", v)}
            fields={[{ key: "name", label: "Name" }, { key: "capacity", label: "Capacity" }, { key: "description", label: "Description", type: "textarea" }, { key: "image_url", label: "Image", type: "image" }]} label="Event spaces" testId="spaces-editor" />
        </TabsContent>

        <TabsContent value="gallery" className="mt-6">
          <div data-testid="gallery-editor">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Gallery media</div>
              <div className="flex items-center gap-2">
                <BulkAddImages testId="gallery-bulk-add" onAdd={(urls) => set("gallery", [...(p.gallery || []), ...urls])} />
                <button type="button" onClick={() => set("gallery", [...(p.gallery || []), ""])} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50" data-testid="gallery-add">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
            </div>
            <div className="mt-4 space-y-4">
              {(p.gallery || []).map((g, i) => (
                <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
                  <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
                    Item {i + 1}
                    <button type="button" onClick={() => set("gallery", p.gallery.filter((_, idx) => idx !== i))} className="text-red-600 hover:underline">Remove</button>
                  </div>
                  <ImageUploader label="" value={g} onChange={(v) => { const n = [...p.gallery]; n[i] = v; set("gallery", n); }} testId={`gallery-item-${i}`} />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <Input value={p.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="YouTube embed URL (https://www.youtube.com/embed/...)" />
          </div>
        </TabsContent>

        <TabsContent value="weddings" className="space-y-4 mt-6">
          <Input value={p.wedding_section?.title || ""} onChange={(e) => setNested("wedding_section", "title", e.target.value)} placeholder="Section title" />
          <Textarea value={p.wedding_section?.description || ""} onChange={(e) => setNested("wedding_section", "description", e.target.value)} rows={5} placeholder="Description" />
          <ImageUploader label="Image" value={p.wedding_section?.image_url} onChange={(v) => setNested("wedding_section", "image_url", v)} testId="wedding-image" />
        </TabsContent>

        <TabsContent value="corporate" className="space-y-4 mt-6">
          <Input value={p.corporate_section?.title || ""} onChange={(e) => setNested("corporate_section", "title", e.target.value)} placeholder="Section title" />
          <Textarea value={p.corporate_section?.description || ""} onChange={(e) => setNested("corporate_section", "description", e.target.value)} rows={5} placeholder="Description" />
          <ImageUploader label="Image" value={p.corporate_section?.image_url} onChange={(v) => setNested("corporate_section", "image_url", v)} testId="corp-image" />
        </TabsContent>

        <TabsContent value="packages" className="mt-6">
          <ArrayEditor items={p.pricing_packages || []} onChange={(v) => set("pricing_packages", v)}
            fields={[{ key: "name", label: "Name" }, { key: "price", label: "Price text" }, { key: "features", label: "Features", type: "array-string" }]} label="Packages" testId="packages-editor" />
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <ArrayEditor items={p.reviews || []} onChange={(v) => set("reviews", v)}
            fields={[{ key: "name", label: "Name" }, { key: "rating", label: "Rating (number)" }, { key: "text", label: "Quote", type: "textarea" }, { key: "source", label: "Source" }]} label="Reviews" testId="reviews-editor" />
        </TabsContent>

        <TabsContent value="faqs" className="mt-6">
          <ArrayEditor items={p.faqs || []} onChange={(v) => set("faqs", v)}
            fields={[{ key: "q", label: "Question" }, { key: "a", label: "Answer", type: "textarea" }]} label="FAQs" testId="faqs-editor" />
        </TabsContent>

        <TabsContent value="seo" className="space-y-4 mt-6">
          <Input value={p.seo?.meta_title || ""} onChange={(e) => setNested("seo", "meta_title", e.target.value)} placeholder="Meta title" />
          <Textarea value={p.seo?.meta_description || ""} onChange={(e) => setNested("seo", "meta_description", e.target.value)} placeholder="Meta description" rows={3} />
          <Input value={p.seo?.keywords || ""} onChange={(e) => setNested("seo", "keywords", e.target.value)} placeholder="Keywords" />
          <ImageUploader imageOnly label="OG image" value={p.seo?.og_image} onChange={(v) => setNested("seo", "og_image", v)} testId="og-image" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
